import { Component, computed, inject, signal } from '@angular/core';
import { AbstractControl, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { RestrictedClient } from '../../../api/app-types/client.app';
import { ENVIRONMENT } from '../../../../environments/environment.interface';
import { TranslocoPipe } from '@jsverse/transloco';
import { LvrButton } from '../../../core/components/lvr-button/lvr-button';
import { MatRadioGroup, MatRadioButton } from '@angular/material/radio';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { matDateProviders } from '../../../shared/helpers/date.func';
import { ImmediateErrorStateMatcher } from '../../../shared/validators/immediate-error-state-matcher';
import { LvrField } from '../../../core/components/field/field';
import { hasError } from '../../../shared/helpers/has-error.func';
import { LvrAppendAsteriskPipe } from '../../../shared/pipes/append-asterisk.pipe';
import { MatFormField } from '@angular/material/form-field';
import { LvrInputDirective } from '../../../core/directives/lvr-input.directive';
import { MatInputModule } from '@angular/material/input';
import { CUSTOM_DATE_FORMATS } from '../../../core/adapters/custom-date-format';
import { DatePipe } from '@angular/common';

export enum AssignmentRequestType {
	Main = 'main',
	Fds = 'fds',
}

export interface RequestClientDialogResult {
	assignmentType: AssignmentRequestType;
	date: Date;
	reason: string;
}

@Component({
	selector: 'lvr-request-client-dialog',
	imports: [
		TranslocoPipe,
		LvrButton,
		MatDialogModule,
		MatRadioGroup,
		MatRadioButton,
		ReactiveFormsModule,
		MatDatepickerModule,
		MatInputModule,
		LvrField,
		LvrAppendAsteriskPipe,
		MatFormField,
		LvrInputDirective,
		DatePipe,
	],
	providers: [matDateProviders],
	templateUrl: './request-client-dialog.html',
	styleUrl: './request-client-dialog.scss',
})
export class RequestClientDialog {
	private readonly fb = inject(FormBuilder);
	private readonly env = inject(ENVIRONMENT);
	public readonly self = inject(MatDialogRef<RequestClientDialog>);
	public readonly client = inject<{ client: RestrictedClient }>(MAT_DIALOG_DATA).client;
	public readonly matcher = new ImmediateErrorStateMatcher();
	public readonly dateDisplayFormat = CUSTOM_DATE_FORMATS.display.dateInput;
	public readonly dateFormatHint = this.env.visualDateFormat;
	public readonly textareaMaxLength = this.env.textareaMaxLength;

	public readonly maxDate = signal(new Date());
	public readonly minDate = computed(() => {
		const date = new Date();
		date.setDate(date.getDate() - this.env.formConfiguration.assignment.minDateOfCommissionOffsetDays);
		return date;
	});

	public get AssignmentRequestType() {
		return AssignmentRequestType;
	}

	public hasError = (control: AbstractControl | null, ...errors: string[]) => hasError(control, ...errors);

	public readonly form = this.fb.group({
		assignmentType: this.fb.control<AssignmentRequestType | null>(null, [Validators.required]),
		date: this.fb.control<Date | null>(null, [Validators.required]),
		reason: this.fb.nonNullable.control(''),
	});

	public submit(): void {
		if (this.form.invalid) {
			this.form.markAllAsTouched();
			return;
		}

		const value = this.form.getRawValue();

		this.self.close({
			assignmentType: value.assignmentType!,
			date: value.date!,
			reason: value.reason,
		} satisfies RequestClientDialogResult);
	}

	public cancel(): void {
		this.self.close();
	}
}
